import { useCallback, useEffect, useRef, useState } from 'react';
import { Application, Container, DisplayObject } from 'pixi.js';
import { zoom as d3zoom, D3ZoomEvent, ZoomTransform } from 'd3-zoom';
import { select } from 'd3-selection';

export interface FOSCanvasStageOptions {
  width?: number;
  height?: number;
}

export function useFOSCanvasStage({
  width: propWidth,
  height: propHeight,
}: FOSCanvasStageOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const zoomRef = useRef<ReturnType<typeof d3zoom<HTMLCanvasElement, unknown>> | null>(null);
  const pointsContainerRef = useRef<Container | null>(null);
  const signalsContainerRef = useRef<Container | null>(null);
  const labelsContainerRef = useRef<Container | null>(null);
  const transformRef = useRef({ x: 0, y: 0, k: 1 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: propWidth || 800,
    height: propHeight || 600,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: newWidth, height: newHeight } = entry.contentRect;
        if (newWidth > 0 && newHeight > 0) {
          setDimensions({ width: newWidth, height: newHeight });
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDimensions({ width: rect.width, height: rect.height });
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || appRef.current) return;

    try {
      const app = new Application({
        width: dimensions.width,
        height: dimensions.height,
        backgroundColor: 0xffffff,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      const canvasElement = app.view as HTMLCanvasElement;
      canvasElement.style.touchAction = 'none';
      canvasElement.style.cursor = 'grab';
      canvasElement.style.display = 'block';
      canvasElement.style.width = '100%';
      canvasElement.style.height = '100%';
      canvasRef.current = canvasElement;
      containerRef.current.appendChild(canvasElement);
      appRef.current = app;

      const pointsContainer = new Container();
      const labelsContainer = new Container();
      const signalsContainer = new Container();
      app.stage.addChild(pointsContainer as DisplayObject);
      app.stage.addChild(labelsContainer as DisplayObject);
      app.stage.addChild(signalsContainer as DisplayObject);
      pointsContainerRef.current = pointsContainer;
      signalsContainerRef.current = signalsContainer;
      labelsContainerRef.current = labelsContainer;

      setIsInitialized(true);
    } catch (err) {
      console.error('[FOSCanvas] Failed to create Pixi.js Application:', err);
      return;
    }

    return () => {
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
      canvasRef.current = null;
      zoomRef.current = null;
      setIsInitialized(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Initialize once; resize handled separately.
  }, []);

  useEffect(() => {
    if (!appRef.current || !isInitialized) return;

    appRef.current.renderer.resize(dimensions.width, dimensions.height);
  }, [dimensions.width, dimensions.height, isInitialized]);

  useEffect(() => {
    if (!canvasRef.current || !isInitialized) return;

    if (!zoomRef.current) {
      zoomRef.current = d3zoom<HTMLCanvasElement, unknown>()
        .scaleExtent([0.5, 10])
        .on('zoom', (event: D3ZoomEvent<HTMLCanvasElement, unknown>) => {
          transformRef.current = {
            x: event.transform.x,
            y: event.transform.y,
            k: event.transform.k,
          };

          for (const pixiContainer of [
            pointsContainerRef.current,
            signalsContainerRef.current,
            labelsContainerRef.current,
          ]) {
            if (pixiContainer) {
              pixiContainer.x = event.transform.x;
              pixiContainer.y = event.transform.y;
              pixiContainer.scale.set(event.transform.k);
            }
          }
        });
    }

    select(canvasRef.current).call(zoomRef.current);

    return () => {
      if (canvasRef.current) {
        select(canvasRef.current).on('.zoom', null);
      }
    };
  }, [isInitialized]);

  const applyZoomTransform = useCallback((transform: ZoomTransform) => {
    if (!canvasRef.current || !zoomRef.current) return;

    select(canvasRef.current)
      .transition()
      .duration(250)
      .call(zoomRef.current.transform as any, transform);
  }, []);

  return {
    containerRef,
    pointsContainerRef,
    signalsContainerRef,
    labelsContainerRef,
    transformRef,
    isInitialized,
    width: dimensions.width,
    height: dimensions.height,
    applyZoomTransform,
  };
}
